import React, { useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, ArrowDownIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
// FR translations for the hero right-column scale labels (text-only, EN untouched).
// "Communications", "Services" and "Transactions" are intentionally kept as-is.
const SCALE_LABELS_FR: Record<string, string> = {
  Identity: 'Identité',
  'ATNA Intelligence': 'Intelligence ATNA'
};
// FR translations for "The foundation. Not a feature." comparison columns.
const TRADITIONAL_FR: Record<string, string> = {
  'Separate logins': 'Connexions séparées',
  'Multiple records': 'Dossiers multiples',
  'Duplicate data': 'Données dupliquées',
  'Fragmented services': 'Services fragmentés',
  'Higher operational costs': 'Coûts opérationnels plus élevés'
};
const FOUNDATION_FR: Record<string, string> = {
  'One verified identity': 'Une identité vérifiée',
  'One verified profile': 'Un profil vérifié',
  'Shared trust layer': 'Couche de confiance partagée',
  'Connected services': 'Services connectés',
  'Lower operational complexity': 'Complexité opérationnelle réduite'
};
// FR translations for the compounding-effect stack items.
// "Communications", "Services", "Analytics" and "ATNA" are intentionally kept as-is.
const STACK_FR: Record<string, string> = {
  Identity: 'Identité'
};
// FR translations for the identity-layer cards.
const CARDS_FR: Record<string, {
  title: string;
  desc: string;
}> = {
  'Digital Identity': {
    title: 'Identité numérique',
    desc: 'Profils et identifiants vérifiés.'
  },
  'Single Sign-On (SSO)': {
    title: 'Authentification unique (SSO)',
    desc: 'Une connexion sécurisée unique pour tous les services connectés.'
  },
  'Multi-Factor Authentication (2FA)': {
    title: 'Authentification multifacteur (2FA)',
    desc: 'Sécurité de niveau entreprise pour chaque compte.'
  },
  'Consent & Audit Trail (Law 25)': {
    title: "Consentement et piste d'audit (Loi 25)",
    desc: 'Conformité à la vie privée et gouvernance des données intégrées.'
  },
  'Open APIs': {
    title: 'API ouvertes',
    desc: 'Connectez les systèmes existants à la couche d’identité.'
  },
  'Identity Governance': {
    title: "Gouvernance de l'identité",
    desc: 'Contrôle centralisé des accès et des permissions.'
  },
  'Profile Management': {
    title: 'Gestion des profils',
    desc: 'Des outils en libre-service pour gérer ses données.'
  },
  'Trusted Data Layer': {
    title: 'Couche de données de confiance',
    desc: "La fondation de données propres requise pour l'IA."
  }
};
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const INCLUDED_CARDS = [{
  title: 'Digital Identity',
  desc: 'Verified profiles and credentials.'
}, {
  title: 'Single Sign-On (SSO)',
  desc: 'One secure login across all connected services.'
}, {
  title: 'Multi-Factor Authentication (2FA)',
  desc: 'Enterprise-grade security for every account.'
}, {
  title: 'Consent & Audit Trail (Law 25)',
  desc: 'Built-in privacy compliance and data governance.'
}, {
  title: 'Open APIs',
  desc: 'Connect existing systems to the identity layer.'
}, {
  title: 'Identity Governance',
  desc: 'Centralized control over access and permissions.'
}, {
  title: 'Profile Management',
  desc: 'Self-service tools for people to manage their data.'
}, {
  title: 'Trusted Data Layer',
  desc: 'The clean data foundation required for AI.'
}];
export function CoreInfrastructure() {
  const {
    lang
  } = useLanguage();
  const scaleLabel = (label: string) => lang === 'FR' ? SCALE_LABELS_FR[label] ?? label : label;
  const tradLabel = (label: string) => lang === 'FR' ? TRADITIONAL_FR[label] ?? label : label;
  const foundationLabel = (label: string) => lang === 'FR' ? FOUNDATION_FR[label] ?? label : label;
  const stackLabel = (label: string) => lang === 'FR' ? STACK_FR[label] ?? label : label;
  const yearLabel = (label: string) => lang === 'FR' ? label.replace(/^Year\b/, 'An') : label;
  const cardTitle = (title: string) => lang === 'FR' ? CARDS_FR[title]?.title ?? title : title;
  const cardDesc = (title: string, desc: string) => lang === 'FR' ? CARDS_FR[title]?.desc ?? desc : desc;
  useEffect(() => {
    track('Page Viewed', {
      page: 'Core Infrastructure'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-1938">
      <Header data-id="element-1939" />

      <main className="pt-[120px]" data-id="element-1940">
        {/* HERO SECTION */}
        <section className="pt-24 pb-32 px-6" data-id="element-1941">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1942">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-1943">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-indigo uppercase mb-6" data-id="element-1944">
                {lang === 'FR' ? 'DÉPLOYEZ UNE FOIS. COMPOSEZ POUR TOUJOURS.' : 'Deploy once. Compose forever.'}
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-tight mb-8" data-id="element-1945">
                {lang === 'FR' ? "L'identité n'est pas une fonctionnalité. C'est l'architecture." : 'Identity is not a feature. It is the architecture.'}
              </h1>
              <p className="font-sans text-xl text-[#121212]/70 leading-relaxed mb-6" data-id="element-1946">
                {lang === 'FR' ? "L'identité BCITI fournit la fondation de confiance pour les communications, les services, les transactions et l'IA." : 'BCITI Identity provides the trusted foundation for communications, services, transactions, and AI.'}
              </p>
              <p className="font-sans text-lg text-[#121212]/60 leading-relaxed mb-10" data-id="element-1947">
                {lang === 'FR' ? 'Sans identité de confiance, chaque nouveau service devient un silo de plus. Avec une identité de confiance, chaque nouveau service renforce la plateforme.' : 'Without trusted identity, every new service becomes another silo. With trusted identity, every new service strengthens the platform.'}
              </p>
              <div className="flex flex-wrap items-center gap-6" data-id="element-1948">
                <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                location: 'Core Hero'
              })} className="inline-flex items-center gap-2 px-8 py-4 bg-indigo text-white text-base font-medium rounded-lg hover:bg-indigo/90 transition-colors" data-id="element-1949">
                  {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-1950" />
                </Link>
                <Link to="/brossard" className="inline-flex items-center gap-2 font-sans text-base font-medium text-[#121212] hover:text-indigo transition-colors" data-id="element-1951">
                  {lang === 'FR' ? 'Découvrir Brossard' : 'Explore Brossard'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-1952" />
                </Link>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative" data-id="element-1953">
              <div className="max-w-sm mx-auto flex flex-col items-center" data-id="element-1954">
                {['Identity', 'Communications', 'Services', 'Transactions', 'ATNA Intelligence'].map((layer, i, arr) => <Fragment key={layer} data-id="element-1955">
                    <div className={`w-full py-5 px-6 text-center border ${i === 0 ? 'border-indigo bg-indigo/5 text-indigo font-medium' : 'border-[#E4E6EC] bg-white text-[#121212]'} font-sans text-lg`} data-id="element-1956">
                      {scaleLabel(layer)}
                    </div>
                    {i < arr.length - 1 && <div className="py-3 text-[#E4E6EC]" data-id="element-1957">
                        <ArrowDownIcon className="w-5 h-5" data-id="element-1958" />
                      </div>}
                  </Fragment>)}
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: THE FOUNDATION, NOT A FEATURE */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1959">
          <div className="max-w-5xl mx-auto" data-id="element-1960">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-20" data-id="element-1961">
              {lang === 'FR' ? 'La fondation. Pas une fonctionnalité.' : 'The foundation. Not a feature.'}
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16" data-id="element-1962">
              <motion.div {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="bg-white border border-[#E4E6EC] p-10" data-id="element-1963">
                <div className="font-sans text-sm font-semibold tracking-widest text-[#121212]/40 uppercase mb-8" data-id="element-1964">
                  {lang === 'FR' ? 'Approche traditionnelle' : 'Traditional Approach'}
                </div>
                <ul className="space-y-6" data-id="element-1965">
                  {['Separate logins', 'Multiple records', 'Duplicate data', 'Fragmented services', 'Higher operational costs'].map(item => <li key={item} className="font-sans text-lg text-[#121212]/60 flex items-start gap-4" data-id="element-1966">
                      <span className="text-[#121212]/20 mt-1" data-id="element-1967">•</span>
                      {tradLabel(item)}
                    </li>)}
                </ul>
              </motion.div>

              <motion.div {...reveal} transition={{
              duration: 0.7,
              delay: 0.2
            }} className="bg-white border border-indigo/20 p-10 relative overflow-hidden" data-id="element-1968">
                <div className="absolute top-0 left-0 w-1 h-full bg-indigo" data-id="element-1969" />
                <div className="font-sans text-sm font-semibold tracking-widest text-indigo uppercase mb-8" data-id="element-1970">
                  {lang === 'FR' ? "Fondation d'identité BCITI" : 'BCITI Identity Foundation'}
                </div>
                <ul className="space-y-6" data-id="element-1971">
                  {['One verified identity', 'One verified profile', 'Shared trust layer', 'Connected services', 'Lower operational complexity'].map(item => <li key={item} className="font-sans text-lg text-[#121212] flex items-start gap-4" data-id="element-1972">
                      <span className="text-indigo mt-1" data-id="element-1973">•</span>
                      {foundationLabel(item)}
                    </li>)}
                </ul>
              </motion.div>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="text-center max-w-3xl mx-auto" data-id="element-1974">
              <p className="font-serif text-2xl text-[#121212] leading-relaxed" data-id="element-1975">
                {lang === 'FR' ? 'Communications, services, paiements et IA reposent tous sur la même fondation de confiance.' : 'Communications, services, payments, and AI all operate on the same trusted foundation.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 3: THE COMPOUNDING EFFECT */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-1976">
          <div className="max-w-6xl mx-auto" data-id="element-1977">
            <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-1978">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-6" data-id="element-1979">
                {lang === 'FR' ? "Déployez l'identité une fois. Étendez pour toujours." : 'Deploy identity once. Expand forever.'}
              </motion.h2>
              <motion.p {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="font-sans text-xl text-[#121212]/60" data-id="element-1980">
                {lang === 'FR' ? "L'identité n'est pas un coût. C'est un actif qui se capitalise." : 'Identity is not a cost. It is an asset that compounds.'}
              </motion.p>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="relative py-12 mb-16 overflow-hidden" data-id="element-1981">
              {/* Subtle upward curve background */}
              <div className="absolute inset-0 border-b border-[#E4E6EC] rounded-[100%] scale-150 translate-y-1/2 opacity-50" data-id="element-1982" />

              <div className="relative z-10 flex flex-col md:flex-row items-end justify-between gap-4 h-[300px]" data-id="element-1983">
                {[{
                year: 'Year 1',
                stack: ['Identity']
              }, {
                year: 'Year 2',
                stack: ['Identity', 'Communications']
              }, {
                year: 'Year 3',
                stack: ['Identity', 'Communications', 'Services']
              }, {
                year: 'Year 4',
                stack: ['Identity', 'Communications', 'Services', 'Analytics']
              }, {
                year: 'Year 5',
                stack: ['Identity', 'Communications', 'Services', 'Analytics', 'ATNA']
              }].map((col, i) => <div key={col.year} className="flex-1 flex flex-col justify-end items-center w-full md:w-auto" data-id="element-1984">
                    <div className="w-full max-w-[180px] flex flex-col-reverse gap-1 mb-4" data-id="element-1985">
                      {col.stack.map((item, j) => <div key={item} className={`w-full py-3 px-2 text-center text-xs font-sans border ${item === 'Identity' ? 'bg-[#121212] text-white border-[#121212]' : item === 'ATNA' ? 'bg-indigo text-white border-indigo' : 'bg-white text-[#121212] border-[#E4E6EC]'}`} data-id="element-1986">
                          {stackLabel(item)}
                        </div>)}
                    </div>
                    <div className="font-sans text-sm text-[#121212]/40 font-medium uppercase tracking-wider" data-id="element-1987">
                      {yearLabel(col.year)}
                    </div>
                  </div>)}
              </div>
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="text-center max-w-2xl mx-auto" data-id="element-1988">
              <p className="font-sans text-lg text-[#121212]/70 mb-4" data-id="element-1989">
                {lang === 'FR' ? "Chaque nouvelle capacité bénéficie de la même couche d'identité de confiance." : 'Each new capability benefits from the same trusted identity layer.'}
              </p>
              <p className="font-sans text-lg text-[#121212] font-medium" data-id="element-1990">
                {lang === 'FR' ? 'La fondation est déployée une fois. La valeur se capitalise avec le temps.' : 'The foundation is deployed once. The value compounds over time.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 4: PROVEN IN PRODUCTION */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1991">
          <div className="max-w-4xl mx-auto" data-id="element-1992">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-16" data-id="element-1993">
              {lang === 'FR' ? 'Éprouvé en production.' : 'Proven in production.'}
            </motion.h2>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="bg-white border border-[#E4E6EC] p-12 relative" data-id="element-1994">
              <div className="absolute -top-4 left-12 bg-indigo text-white font-sans text-xs font-bold tracking-widest uppercase px-4 py-2" data-id="element-1995">
                {lang === 'FR' ? 'Pas un projet pilote' : 'Not a pilot'}
              </div>

              <div className="mb-12" data-id="element-1996">
                <h3 className="font-serif text-3xl text-[#121212] mb-2" data-id="element-1997">
                  {lang === 'FR' ? 'Ville de Brossard' : 'City of Brossard'}
                </h3>
                <p className="font-sans text-[#121212]/60" data-id="element-1998">
                  {lang === 'FR' ? 'Au service des communautés qui comptent sur un engagement citoyen vérifié au quotidien.' : 'Trusted by communities that rely on verified citizen engagement every day.'}
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12" data-id="element-1999">
                <div data-id="element-2000">
                  <div className="font-serif text-3xl text-[#121212] mb-2" data-id="element-2001">
                    88,266
                  </div>
                  <div className="font-sans text-sm text-[#121212]/60" data-id="element-2002">
                    {lang === 'FR' ? 'Identités vérifiées' : 'Verified Identities'}
                  </div>
                </div>
                <div data-id="element-2003">
                  <div className="font-serif text-3xl text-[#121212] mb-2" data-id="element-2004">
                    78%
                  </div>
                  <div className="font-sans text-sm text-[#121212]/60" data-id="element-2005">
                    {lang === 'FR' ? 'Couverture de la population' : 'Population Coverage'}
                  </div>
                </div>
                <div data-id="element-2006">
                  <div className="font-serif text-3xl text-[#121212] mb-2" data-id="element-2007">
                    Since 2017
                  </div>
                  <div className="font-sans text-sm text-[#121212]/60" data-id="element-2008">
                    {lang === 'FR' ? '9 ans en production' : '9 Years in Production'}
                  </div>
                </div>
                <div data-id="element-2009">
                  <div className="font-serif text-3xl text-[#121212] mb-2" data-id="element-2010">
                    20+
                  </div>
                  <div className="font-sans text-sm text-[#121212]/60" data-id="element-2011">
                    {lang === 'FR' ? 'Communautés' : 'Communities'}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-8 border-t border-[#E4E6EC]" data-id="element-2012">
                <div className="flex items-center gap-6 font-sans text-sm text-[#121212]/50" data-id="element-2013">
                  <span data-id="element-2014">
                    {lang === 'FR' ? 'Conforme Loi 25' : 'Law 25 Ready'}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-[#E4E6EC]" data-id="element-2015" />
                  <span data-id="element-2016">
                    {lang === 'FR' ? 'Infrastructure hébergée au Canada' : 'Canadian Hosted Infrastructure'}
                  </span>
                </div>
                <Link to="/brossard" className="inline-flex items-center gap-2 font-sans text-sm font-medium text-indigo hover:text-indigo/80 transition-colors" data-id="element-2017">
                  {lang === 'FR' ? "Découvrir l'étude de cas Brossard" : 'Explore the Brossard Case Study'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-2018" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 5: WHAT IS INCLUDED */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-2019">
          <div className="max-w-6xl mx-auto" data-id="element-2020">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-20" data-id="element-2021">
              {lang === 'FR' ? "Que contient la couche d'identité ?" : 'What is inside the identity layer?'}
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2022">
              {INCLUDED_CARDS.map((card, i) => <motion.div key={card.title} {...reveal} transition={{
              duration: 0.5,
              delay: i * 0.05
            }} className="border border-[#E4E6EC] p-8 hover:border-indigo/30 transition-colors" data-id="element-2023">
                  <h3 className="font-serif text-xl text-[#121212] mb-3" data-id="element-2024">
                    {cardTitle(card.title)}
                  </h3>
                  <p className="font-sans text-sm text-[#121212]/60 leading-relaxed" data-id="element-2025">
                    {cardDesc(card.title, card.desc)}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* SECTION 6: THE COST OF WAITING */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2026">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2027">
            <motion.h2 {...reveal} className="font-serif text-3xl md:text-4xl text-[#121212] mb-10" data-id="element-2028">
              {lang === 'FR' ? 'Les fondations définissent les marchés.' : 'Foundations define markets.'}
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="space-y-6 font-sans text-lg text-[#121212]/70 leading-relaxed" data-id="element-2029">
              <p data-id="element-2030">
                {lang === 'FR' ? "Les organisations qui déploient une identité de confiance aujourd'hui créent la plateforme à laquelle les autres devront se connecter demain." : 'Organizations that deploy trusted identity today create the platform others must connect to tomorrow.'}
              </p>
              <p data-id="element-2031">
                {lang === 'FR' ? "L'infrastructure d'identité gagne en valeur à mesure que l'adoption croît." : 'Identity infrastructure becomes more valuable as adoption grows.'}
              </p>
              <p data-id="element-2032">
                {lang === 'FR' ? 'Une fondation bâtie aujourd’hui devient de plus en plus difficile à remplacer avec le temps.' : 'A foundation built today becomes increasingly difficult to replace over time.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 7: FINAL CTA */}
        <section className="py-40 px-6 border-t border-[#E4E6EC]" data-id="element-2033">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2034">
            <motion.h2 {...reveal} className="font-serif text-5xl text-[#121212] mb-8" data-id="element-2035">
              {lang === 'FR' ? 'Bâtissez sur une fondation de confiance.' : 'Build on a trusted foundation.'}
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="mb-12" data-id="element-2036">
              <p className="font-sans text-xl text-[#121212]/60 mb-2" data-id="element-2037">
                {lang === 'FR' ? 'Communications. Services. Transactions. IA.' : 'Communications. Services. Transactions. AI.'}
              </p>
              <p className="font-sans text-xl text-[#121212] font-medium" data-id="element-2038">
                {lang === 'FR' ? "Tout commence par l'identité." : 'Everything starts with identity.'}
              </p>
            </motion.div>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} data-id="element-2039">
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Core Final'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-indigo text-white text-base font-medium rounded-lg hover:bg-indigo/90 transition-colors" data-id="element-2040">
                {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-2041" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-2042" />
    </div>;
}